import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { usePasskey as usePasskeyCeremony } from '@htmniseko/htm-passkey-client';
import { passkeyStepUpOptions, passkeyStepUpVerify } from 'state/modules/verification';

/**
 * Session-scoped passkey step-up hook.
 *
 * Wraps the shared @htmniseko/htm-passkey-client hook (same WebAuthn ceremony
 * as the login-time flow) but points at owner-api's /owners/passkey/step-up/*
 * endpoints instead of /passkey/authenticate/*. Those step-up endpoints:
 *   - restrict allowCredentials to the current session user, so a different
 *     owner's passkey on the same device cannot be offered by the browser,
 *   - assert the verified credential belongs to the session user, and
 *   - return { verified: true } without minting a new session token — the
 *     current session stays intact instead of being swapped on verify.
 */
const usePasskeyStepUp = () => {
  const dispatch = useDispatch();

  const client = useMemo(() => ({
    getAuthenticationOptions: () => dispatch(passkeyStepUpOptions()),
    verifyAuthentication: async credential => {
      const result = await dispatch(passkeyStepUpVerify(credential));
      return { verified: result?.verified === true };
    },
    // Registration is intentionally unsupported here — step-up is
    // authenticate-only. The shared hook exposes register() regardless; the
    // caller (VerificationRequired) only calls authenticate().
    getRegistrationOptions: () => Promise.reject(new Error('Not supported in step-up')),
    verifyRegistration: () => Promise.reject(new Error('Not supported in step-up')),
    listCredentials: () => Promise.resolve([]),
    removeCredential: () => Promise.resolve(),
  }), [dispatch]);

  return usePasskeyCeremony(client);
};

export default usePasskeyStepUp;
